import { Box, Button, Checkbox, Chip, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import { useEffect, useMemo, useState } from 'react';
import { baseImageUrl } from '../../../utils/config';

const mediaUrl = (value) => {
  if (!value) return '';
  if (/^(blob:|data:|https?:\/\/)/i.test(value)) return value;
  return `${baseImageUrl}${value}`;
};

const money = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(value || 0));

const PreviewImage = ({ src, alt, sx }) => src
  ? <Box component="img" src={mediaUrl(src)} alt={alt} sx={{ objectFit: 'cover', bgcolor: '#edf4fa', ...sx }} />
  : <Box sx={{ display: 'grid', placeItems: 'center', bgcolor: '#edf5fc', color: '#1976d2', ...sx }}><CleaningServicesRoundedIcon /></Box>;

export default function ServiceLivePreview({ service, packages = [], extras = [] }) {
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedExtraIds, setSelectedExtraIds] = useState([]);

  useEffect(() => {
    const firstPackage = packages[0];
    setSelectedPackageId((current) => packages.some((item) => item.id === current) ? current : firstPackage?.id || null);
  }, [packages]);

  const selectedPackage = packages.find((item) => item.id === selectedPackageId) || packages[0];
  const packageItems = useMemo(() => selectedPackage?.item || [], [selectedPackage]);
  const selectedItems = useMemo(
    () => packageItems.filter((item) => selectedItemIds.includes(item.id)),
    [packageItems, selectedItemIds]
  );
  const selectedExtras = useMemo(
    () => extras.filter((item) => selectedExtraIds.includes(item.id)),
    [extras, selectedExtraIds]
  );
  const itemsTotal = selectedItems.reduce((total, item) => total + Number(item?.price || 0), 0);
  const extrasTotal = selectedExtras.reduce((total, item) => total + Number(item?.price || 0), 0);
  const accent = service?.colour || '#1976d2';

  const toggle = (id, setter) => setter((current) => current.includes(id)
    ? current.filter((itemId) => itemId !== id)
    : [...current, id]);

  return (
    <Box sx={{ border: '1px solid #dce8f5', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 14px 38px rgba(28,72,120,.09)' }}>
      <Box sx={{ px: 2, py: 1.6, display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#f7fbff', borderBottom: '1px solid #e5edf6' }}>
        <Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#e8f3ff', color: '#1976d2' }}><CheckCircleRoundedIcon fontSize="small" /></Box>
        <Box sx={{ flex: 1 }}><Typography sx={{ color: '#10224a', fontSize: 15, fontWeight: 850 }}>Live Booking Preview</Typography><Typography sx={{ color: '#718096', fontSize: 10.5 }}>Reflects the current saved service configuration.</Typography></Box>
        <Chip size="small" label="LIVE" sx={{ height: 23, bgcolor: '#eaf8ef', color: '#168447', fontSize: 9.5, fontWeight: 800 }} />
      </Box>

      <Box sx={{ p: 1.6 }}>
        <PreviewImage src={service?.image_1} alt={service?.title || 'Service'} sx={{ width: '100%', height: 174, borderRadius: 2.2 }} />
        <Box sx={{ width: 42, height: 4, mt: 1.2, mb: 1, borderRadius: 3, bgcolor: accent }} />
        <Typography sx={{ color: '#0d2250', fontSize: 21, lineHeight: 1.15, fontWeight: 900 }}>{service?.title || service?.name || 'Service preview'}</Typography>
        <Typography sx={{ mt: .7, color: '#60708e', fontSize: 11.5, lineHeight: 1.55 }}>{service?.description || 'Add a service description to show customers what is included.'}</Typography>

        <Typography sx={{ mt: 2, mb: .9, color: '#10224a', fontSize: 13, fontWeight: 850 }}>Choose a Package</Typography>
        {packages.length ? <Box sx={{ display: 'grid', gridTemplateColumns: packages.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: .8 }}>
          {packages.map((item) => {
            const selected = item.id === selectedPackage?.id;
            return <Box key={item.id} onClick={() => { setSelectedPackageId(item.id); setSelectedItemIds([]); }} sx={{ p: .9, display: 'flex', alignItems: 'center', gap: .8, border: `1.5px solid ${selected ? accent : '#dce5ef'}`, borderRadius: 2, cursor: 'pointer', bgcolor: selected ? '#f7fbff' : '#fff' }}>
              <PreviewImage src={item.image_12pack} alt={item.title} sx={{ width: 48, height: 38, borderRadius: 1.2 }} />
              <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ color: '#183153', fontSize: 10.5, fontWeight: 800 }}>{item.title}</Typography><Typography sx={{ color: '#718096', fontSize: 9 }}>{item.item?.length || 0} items · {item.front_end_field_type || 'selection'}</Typography></Box>
            </Box>;
          })}
        </Box> : <Box sx={{ p: 2, border: '1px dashed #cbd9e8', borderRadius: 2, textAlign: 'center', color: '#718096', fontSize: 11 }}>No packages have been added.</Box>}

        {!!packageItems.length && <>
          <Typography sx={{ mt: 1.7, mb: .7, color: '#10224a', fontSize: 12.5, fontWeight: 850 }}>Select Items</Typography>
          <Box sx={{ border: '1px solid #e2eaf3', borderRadius: 2, overflow: 'hidden' }}>
            {packageItems.map((item, index) => <Box key={item.id} onClick={() => toggle(item.id, setSelectedItemIds)} sx={{ px: .8, py: .7, display: 'flex', alignItems: 'center', gap: .6, cursor: 'pointer', borderTop: index ? '1px solid #edf1f6' : 0 }}>
              <Checkbox size="small" checked={selectedItemIds.includes(item.id)} tabIndex={-1} sx={{ p: .3, color: accent, '&.Mui-checked': { color: accent } }} />
              <PreviewImage src={item.image_12 || item.image} alt={item.title} sx={{ width: 38, height: 30, borderRadius: 1 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}><Typography noWrap sx={{ color: '#183153', fontSize: 10.5, fontWeight: 800 }}>{item.title || item.name}</Typography><Typography sx={{ color: '#718096', fontSize: 9 }}>{item.time_hrs || 0} hours</Typography></Box>
              <Typography sx={{ color: '#183153', fontSize: 10.5, fontWeight: 850 }}>{money(item.price)}</Typography>
            </Box>)}
          </Box>
        </>}

        {!!extras.length && <>
          <Typography sx={{ mt: 1.7, mb: .7, color: '#10224a', fontSize: 12.5, fontWeight: 850 }}>Optional Extras</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: .8 }}>
            {extras.map((item) => <Box key={item.id} onClick={() => toggle(item.id, setSelectedExtraIds)} sx={{ p: .8, display: 'flex', alignItems: 'center', gap: .6, border: `1px solid ${selectedExtraIds.includes(item.id) ? accent : '#dce5ef'}`, borderRadius: 1.7, cursor: 'pointer' }}>
              <Checkbox size="small" checked={selectedExtraIds.includes(item.id)} tabIndex={-1} sx={{ p: 0, color: accent, '&.Mui-checked': { color: accent } }} />
              <PreviewImage src={item.image_12} alt={item.title} sx={{ width: 34, height: 34, borderRadius: 1 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}><Typography noWrap sx={{ color: '#183153', fontSize: 9.5, fontWeight: 800 }}>{item.title}</Typography><Typography sx={{ color: '#1976d2', fontSize: 10, fontWeight: 800 }}>{money(item.price)}</Typography></Box>
            </Box>)}
          </Box>
        </>}

        <Box sx={{ mt: 1.7, p: 1.2, borderRadius: 2, bgcolor: '#f7faff', border: '1px solid #e3ebf5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 10.5, color: '#60708e' }}>Items ({selectedItems.length})</Typography><Typography sx={{ fontSize: 10.5, fontWeight: 750 }}>{money(itemsTotal)}</Typography></Box>
          <Box sx={{ mt: .45, display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 10.5, color: '#60708e' }}>Extras ({selectedExtras.length})</Typography><Typography sx={{ fontSize: 10.5, fontWeight: 750 }}>{money(extrasTotal)}</Typography></Box>
          <Box sx={{ mt: .8, pt: .8, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #dce6f0' }}><Typography sx={{ fontSize: 12, fontWeight: 850 }}>Subtotal</Typography><Typography sx={{ color: accent, fontSize: 14, fontWeight: 900 }}>{money(itemsTotal + extrasTotal)}</Typography></Box>
        </Box>
        <Button fullWidth variant="contained" disabled sx={{ mt: 1.1, minHeight: 39, borderRadius: 2, textTransform: 'none', fontWeight: 800, bgcolor: accent }}>Preview only</Button>
      </Box>
    </Box>
  );
}
